import TranslatableText from "@components/translations/TranslatableText";
import ConfirmNavigation from "@components/confirmNavigation/ConfirmNavigation";
import useErrorMessage from "@utils/useErrorMessage";
import ProjectDescriptionSubtitle from "./ProjectDescriptionSubtitle";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import ModuleButton from "@components/button/ModuleButton";
import ProjectContentHeader from "../ProjectContentHeader";

import * as utils from "./projectDescriptionUtils";
import {
	useNavigate,
	unstable_useBlocker as useBlocker,
} from "react-router-dom";
import {
	useCreateProjectMutation,
	useGetSOCrefQuery,
	useUpdateProjectMutation,
} from "@hooks/index";
import type { FormikHelpers } from "formik";
import { Formik, useFormikContext, Form } from "formik";
import { FieldType, type DropdownInputGroup, type InputGroup } from "@typedefs/modulesInterfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	useCreateExecutingAgencyMutation,
	useCreateFundingAgencyMutation,
	useGetCountriesQuery,
	useGetExecutingAgenciesQuery,
	useGetFundingAgenciesQuery,
	useGetGWPSourcesQuery,
} from "@hooks/index";
import type { ProjectPayload } from "@features/project/projectTypes";
import { Actions, type Project } from "@typedefs/interfaces";
import { useAppDispatch, useAppSelector, useClimateAndMoistureFormHandlers, usePermissions } from "@app/hooks";
import { setProject, selectTemporaryMapData } from "@features/project/projectSlice";
import { preventFormSubmit, isNumeric } from "@pages/activityBuilder/modules/inputs/utils";
import { TranslatedFormSubmodule } from "@components/formSubmodule/TranslatedFormSubmodule";
import {
	selectCurrentActions,
	setIsFetchingData,
} from "@features/builder/builderSlice";
import { getCapitalizationYears } from "@utils/helperFunctions";
import { BiSave } from "react-icons/bi";
import { useHandleProjectState } from "../useHandleProjectState";
import { EventTypes, useFirebaseHook } from "../../../utils/firebaseUtils";
import { useClimateConfigurationValidation } from "./projectDescriptionUtils";

const ProjectDescriptionContainer = () => {
	const [
		createProject,
		{ isLoading: isCreateLoading, isError: isCreateError, error: createError },
	] = useCreateProjectMutation();
	const [
		updateProject,
		{ isLoading: isUpdateLoading, isError: isUpdateError, error: updateError },
	] = useUpdateProjectMutation();
	const { project, errorMsg, isLoading: isGetProjectLoading, projectId } = useHandleProjectState();

	const navigate = useNavigate();
	const [localProject, setLocalProject] = useState<Project | null>();
	const [formInitValues, setFormInitValues] = useState<utils.ProjectForm>(
		utils.initialValues,
	);
	const dispatch = useAppDispatch();
	const { fireEvent } = useFirebaseHook();
	const temporaryMapData = useAppSelector(selectTemporaryMapData);

	useEffect(() => {
		if (project)
			setLocalProject(project);
	}, [project]);

	const isExistingProject = useMemo<boolean>(
		() => !!projectId && isNumeric(projectId) && !!localProject,
		[projectId, localProject],
	);

	useEffect(() => {
		dispatch(setIsFetchingData(isGetProjectLoading));
		return () => {
			dispatch(setIsFetchingData(false));
		};
	}, [dispatch, isGetProjectLoading]);

	useEffect(() => {
		isExistingProject
			? setFormInitValues({
				name: localProject?.name ?? "",
				code: localProject?.code ?? "",
				cost: localProject?.cost ?? null,
				funding_agency: localProject?.funding_agency
					? localProject?.funding_agency
					: null,
				executing_agency: localProject?.executing_agency
					? localProject?.executing_agency
					: null,
				country: localProject?.country?.id ?? null,
				climate: localProject?.climate?.id ?? null,
				moisture: localProject?.moisture?.id ?? null,
				soil_type: localProject?.soil_type?.id ?? null,
				implementation_years: localProject?.implementation_years ?? null,
				start_year_of_activities: localProject?.start_year_of_activities ?? null,
				last_year_of_accounting: localProject?.last_year_of_accounting ?? null,
				// capitalization_years: localProject?.capitalization_years ?? null,
				total_duration_of_accounting: null,
				soc_ref_t2: localProject?.soc_ref_t2 ?? null,
				gw_potential: { source: localProject?.gw_potential?.id ?? -1 },
				status: localProject?.status?.id ?? null,
			})
			: setFormInitValues(utils.initialValues);
	}, [isExistingProject, localProject]);

	const { errorMsg: createErrorMsg } = useErrorMessage({
		isError: isCreateError,
		error: createError,
	});
	const { errorMsg: updateErrorMsg } = useErrorMessage({
		isError: isUpdateError,
		error: updateError,
	});

	const handleSubmit = async (
		values: utils.ProjectForm,
		{ resetForm }: FormikHelpers<utils.ProjectForm>,
	) => {
		/* -1 are just for typescript, validation is done before handleSubmit is called */
		const payload: ProjectPayload = {
			name: values.name,
			code: values.code,
			cost: values.cost,
			funding_agency: values.funding_agency, //IN REALTA' SONO STRING AD ORA
			executing_agency: values.executing_agency,
			country: values.country ?? -1,
			climate: values.climate ?? -1,
			moisture: values.moisture ?? -1,
			soil_type: values.soil_type ?? -1,
			implementation_years: values.implementation_years ?? -1,
			start_year_of_activities: values.start_year_of_activities ?? -1,
			last_year_of_accounting: values.last_year_of_accounting ?? -1,
			soc_ref_t2: values.soc_ref_t2,
			gw_potential: values.gw_potential.source,
			status: values.status,
		};

		if (isExistingProject) {
			updateProject({
				id: Number(projectId),
				body: payload,
			})
				.unwrap()
				.then((project) => {
					resetForm({ values });
					dispatch(setProject(project));
					fireEvent(EventTypes.edit_project)
					setTimeout(
						() => navigate(`/project/${project.id}/activities`),
						50,
					);
				})
				.catch((err) => console.error(err))
		} else {
			createProject(temporaryMapData ? { ...payload, map_data: JSON.stringify(temporaryMapData) } : payload)
				.unwrap()
				.then((project) => {
					resetForm({ values });
					dispatch(setProject(project));
					fireEvent(EventTypes.create_project)
					setTimeout(
						() => {
							const url = `/project/${project.id}/activities`;
							navigate(url);
						},
						50,
					);
				})
				.catch((err) => console.error(err));
		};
	};

	const actions = useAppSelector(selectCurrentActions);
	const includeNotes = actions?.includes(Actions.Notes);

	return errorMsg ? (
		<section>
			<div className="module-group">
				<span className="error fs-13 pb-2">
					{errorMsg
						? errorMsg
						: `An error occurred while fetching for project ${projectId}`}
				</span>
			</div>
		</section>
	) : (
		<Formik
			initialValues={formInitValues}
			validationSchema={utils.validationSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<section>
				<Form onKeyDown={preventFormSubmit}>
					<div className="module-group">
						<ProjectContentHeader project={project} includeNotes={includeNotes} title="project.project_description" />

						<DescriptionSubmodule />
						<SiteSubmodule />
						<ReferenceSoilSubmodule />
						<DurationSubmodule />
						<GlobalWarmingSubmodule />
					</div>

					<NavBlocker />
					<FormFooter
						isLoading={isCreateLoading || isUpdateLoading || isGetProjectLoading}
						isExistingProject={isExistingProject}
						errorMessage={createErrorMsg || updateErrorMsg}
					/>
				</Form>
			</section>
		</Formik>
	);
};

const DescriptionSubmodule = () => {
	const { setErrors, errors } = useFormikContext();
	const { isReadOnly } = usePermissions();

	const { data: fundingAgencies } = useGetFundingAgenciesQuery();
	const { data: executingAgencies } = useGetExecutingAgenciesQuery();

	const [createExec] = useCreateExecutingAgencyMutation();
	const [createFund] = useCreateFundingAgencyMutation();

	const handleCreateExec = async (newOption: string) =>
		await createExec(newOption)
			.unwrap()
			.then((option) => option)
			.catch((err) => {
				setErrors({
					...errors,
					test_executing_agency: "An error occurred while creating the agency",
				});
			});

	const handleCreateFund = async (newOption: string) =>
		await createFund(newOption)
			.unwrap()
			.then((option) => option)
			.catch((err) => {
				setErrors({
					...errors,
					test_funding_agency: "An error occurred while creating the agency",
				});
			});

	return (
		<TranslatedFormSubmodule submoduleName="project.project_description" className="pt-2">
			<ProjectDescriptionSubtitle translationKey="project.project_description_subtitle" />
			<div className="pt-1" />
			<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.projectTitle, disabled: true } : utils.projectTitle} />
			<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
				<div style={{ gridColumn: 1 }} className="pe-2">
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.projectCode, disabled: true } : utils.projectCode} />
				</div>
				<div style={{ gridColumn: 2 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.projectCost, disabled: true } : utils.projectCost} />
				</div>
			</div>
			<PaddedGenericInputGroup
				inputGroup={
					{
						...utils.fundingAgency,
						onCreate: handleCreateFund,
						dropdownOptions: fundingAgencies,
						disabled: isReadOnly,
					} as InputGroup
				}

			/>
			<PaddedGenericInputGroup
				inputGroup={
					{
						...utils.executingAgency,
						onCreate: handleCreateExec,
						dropdownOptions: executingAgencies,
						disabled: isReadOnly,
					} as InputGroup
				}

			/>
			<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.status, disabled: true } : utils.status} />
		</TranslatedFormSubmodule>
	);
};

const SiteSubmodule = () => {
	const { isReadOnly } = usePermissions();
	const { values, setFieldError } = useFormikContext<utils.ProjectForm>();
	const { data: countries } = useGetCountriesQuery();
	const { handleClimateInputChange, getMoistureOptions, climateTypes } = useClimateAndMoistureFormHandlers();

	const { isValid, shouldValidate } = useClimateConfigurationValidation(
		values.climate,
		values.moisture,
		values.soil_type
	);

	useEffect(() => {
		if (shouldValidate && !isValid) {
			setFieldError('climate', 'validations.invalid_soc_configuration');
			setFieldError('moisture', 'validations.invalid_soc_configuration');
			setFieldError('soil_type', 'validations.invalid_soc_configuration');
		} else {
			setFieldError('climate', undefined);
			setFieldError('moisture', undefined);
			setFieldError('soil_type', undefined);
		}
	}, [isValid, shouldValidate, setFieldError]);

	const moistureInput: DropdownInputGroup = useMemo(() => ({
		type: FieldType.SELECT,
		inputName: "moisture",
		label: "project.moisture",
		dropdownOptions: getMoistureOptions(values.climate),
	}), [values.climate, getMoistureOptions]);

	const climateInput: DropdownInputGroup = {
		type: FieldType.SELECT,
		inputName: "climate",
		label: "project.climate",
		dropdownOptions: climateTypes ?? [],
		onChange: (e: any) => {
			handleClimateInputChange(e, values.moisture, "moisture");
		}
	}

	return (
		<TranslatedFormSubmodule submoduleName="project.project_site">
			<ProjectDescriptionSubtitle translationKey="project.project_site_subtitle" />
			<div className="pt-1">
				<PaddedGenericInputGroup
					inputGroup={
						countries
							? { ...utils.country, dropdownOptions: countries, disabled: isReadOnly }
							: utils.country
					}
				/>
				<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...climateInput, disabled: true } : climateInput} />
				<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...moistureInput, disabled: true } : moistureInput} />
				<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.soilType, disabled: true } : utils.soilType} />
				{shouldValidate && !isValid && (
					<div className="text-error fs-13 pt-1">
						<TranslatableText translationKey="validations.invalid_soc_configuration" />
					</div>
				)}
			</div>
		</TranslatedFormSubmodule>
	);
};

const DurationSubmodule = () => {
	const { isReadOnly } = usePermissions();
	const { values, setFieldValue } = useFormikContext<utils.ProjectForm>();

	useEffect(() => {
		if (values.start_year_of_activities && values.last_year_of_accounting && values.implementation_years) {
			setFieldValue("capitalization_years", getCapitalizationYears(values.start_year_of_activities, values.implementation_years, values.last_year_of_accounting));
		}
	}, [setFieldValue, values.implementation_years, values.last_year_of_accounting, values.start_year_of_activities]);

	return (
		<TranslatedFormSubmodule submoduleName="project.project_duration">
			<ProjectDescriptionSubtitle translationKey="project.project_duration_subtitle" />
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 2fr",
					gridTemplateRows: "repeat(4, auto)",
				}}
				className="pt-1"
			>
				<div
					style={{ gridColumn: 1, gridRow: "1 / span 4" }}
					className="d-flex f-column pe-2 pt-1"
				>
					<span className="fs-13">Project Duration</span>
					<span className="fs-12 ff-light">[years]</span>
				</div>
				<div style={{ gridColumn: 2, gridRow: 1 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.startYearOfActivities, disabled: true } : utils.startYearOfActivities} />
				</div>
				<div style={{ gridColumn: 2, gridRow: 2 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.implementationPhase, disabled: true } : utils.implementationPhase} />
				</div>
				<div style={{ gridColumn: 2, gridRow: 3 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.lastYearOfAccounting, disabled: true } : utils.lastYearOfAccounting} />
				</div>
				<div style={{ gridColumn: 2, gridRow: 4 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.capitalizationPhase, disabled: true } : utils.capitalizationPhase} />
				</div>
			</div>
		</TranslatedFormSubmodule>
	);
};

const GlobalWarmingSubmodule = () => {
	const { values, setFieldValue } = useFormikContext<utils.ProjectForm>();
	const { data: gwpOptions } = useGetGWPSourcesQuery();
	const { isReadOnly } = usePermissions();

	useEffect(() => {
		if (gwpOptions?.length) {
			const gwp = gwpOptions.find(
				(option) => option.id === values.gw_potential.source,
			);
			setFieldValue("gw_potential.co2", gwp?.co2);
			setFieldValue("gw_potential.ch4", gwp?.ch4);
			setFieldValue("gw_potential.n2o", gwp?.n2o);
		}
	}, [values.gw_potential, gwpOptions, setFieldValue]);

	return (
		<TranslatedFormSubmodule submoduleName="project.global_warming_potential">
			<ProjectDescriptionSubtitle translationKey="project.global_warming_potential_subtitle" />
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "2fr 4fr",
					gridTemplateRows: "repeat(4, auto)",
				}}
				className="pt-1"
			>
				<div style={{ gridColumn: "1 / span 2" }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.source, disabled: true } : utils.source} />
				</div>
				<div style={{ gridColumn: 2, gridRow: 2 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.co2, disabled: true } : utils.co2} />
				</div>
				<div style={{ gridColumn: 2, gridRow: 3 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.ch4, disabled: true } : utils.ch4} />
				</div>
				<div style={{ gridColumn: 2, gridRow: 4 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.n2o, disabled: true } : utils.n2o} />
				</div>
			</div>
		</TranslatedFormSubmodule>
	);
};

const ReferenceSoilSubmodule = () => {
	const [climate, setClimate] = useState<number | null>(null);
	const [moisture, setMoisture] = useState<number | null>(null);
	const [soilType, setSoilType] = useState<number | null>(null);
	const { isReadOnly } = usePermissions();
	const { values, setFieldValue } = useFormikContext<utils.ProjectForm>();
	const { data: socRef } = useGetSOCrefQuery(
		{ climate, moisture, soil_type: soilType },
		{
			refetchOnMountOrArgChange: true,
		},
	);

	useEffect(() => {
		setClimate(values.climate ? values.climate : null);
	}, [values.climate]);
	useEffect(() => {
		setMoisture(values.moisture ? values.moisture : null);
	}, [values.moisture]);
	useEffect(() => {
		setSoilType(values.soil_type ? values.soil_type : null);
	}, [values.soil_type]);

	useEffect(() => {
		setFieldValue("soc_ref", socRef?.value);
	}, [socRef, setFieldValue]);

	return (
		<TranslatedFormSubmodule submoduleName="project.reference_soil_organic_carbon_stock">
			<ProjectDescriptionSubtitle translationKey="project.reference_soil_organic_carbon_stock_subtitle" />
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "2fr 4fr",
					gridTemplateRows: "repeat(4, auto)",
				}}
				className="pt-1"
			>
				<div
					style={{ gridColumn: 1, gridRow: 1 }}
					className="d-flex f-column pe-2 pt-1"
				>
					<span className="fs-13">SOCref</span>
					<span className="fs-12 ff-light">[tC/ha]</span>
				</div>
				<div
					style={{ gridColumn: 1, gridRow: 2 }}
					className="d-flex f-column pe-2 pt-1"
				>
					<span className="fs-13">SOCref Tier 2</span>
					<span className="fs-12 ff-light">[tC/ha]</span>
				</div>
				<div style={{ gridColumn: 2, gridRow: 1 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.socref, disabled: true } : utils.socref} />
				</div>
				<div style={{ gridColumn: 2, gridRow: 2 }}>
					<PaddedGenericInputGroup inputGroup={isReadOnly ? { ...utils.socreft2, disabled: true } : utils.socreft2} />
				</div>
			</div>
		</TranslatedFormSubmodule>
	);
};

const NavBlocker = () => {
	const { dirty, touched } = useFormikContext();

	const shouldBlock = useCallback(
		() => Object.keys(touched).length > 0 && dirty,
		[dirty, touched],
	);
	const blocker = useBlocker(shouldBlock);

	return <ConfirmNavigation blocker={blocker} />;
};

const FormFooter = ({
	isLoading,
	isExistingProject,
	errorMessage
}: {
	isLoading: boolean;
	isExistingProject?: boolean;
	errorMessage?: string
}) => {
	const { dirty } = useFormikContext<utils.ProjectForm>();
	const { canMutateProject, isReadOnly } = usePermissions()
	const { values } = useFormikContext<utils.ProjectForm>();
	const { isValid } = useClimateConfigurationValidation(
		values.climate,
		values.moisture,
		values.soil_type
	);

	return (
		<footer className="module-group-footer">
			<div className="module-group">
				<p className="text-error fs-13 mb-1">
					{errorMessage}
				</p>
				{!isReadOnly ?
					<ModuleButton icon={BiSave} isLoading={isLoading} disabled={!dirty || !canMutateProject || !isValid} labelKey={isExistingProject ? "project.update_project" : "project.create_project"} />
					: null}
			</div>
		</footer>
	);
};

const PaddedGenericInputGroup = ({
	inputGroup,
}: {
	inputGroup: InputGroup;
}) => {
	const { canMutateProject } = usePermissions();
	const isDisabled = !canMutateProject || inputGroup.disabled;

	return (
		<div className="py-1">
			<GenericInputGroup inputGroup={{ ...inputGroup, disabled: isDisabled }} />
		</div>
	);
};

export default ProjectDescriptionContainer;
